// src/components/home/awareness/awarenessStorage.js

const AWARENESS_STORAGE_PREFIX = 'bf_awareness';

const GLOBAL_GAP_CAMPAIGN_KEY = 'global_gap';

/**
 * Normalises usernames so every account gets stable,
 * account-specific storage keys.
 */
const normaliseUsername = (username) => {
  return String(username || '')
    .trim()
    .toLowerCase();
};

/**
 * Builds one localStorage key for a campaign and user.
 *
 * Examples:
 * bf_awareness:reward_code:sizwemondlane
 * bf_awareness:profile_banner:sizwemondlane
 * bf_awareness:global_gap:sizwemondlane
 */
export const getAwarenessStorageKey = (
  campaignKey,
  username
) => {
  const safeCampaignKey = String(campaignKey || '')
    .trim()
    .toLowerCase();

  const safeUsername = normaliseUsername(username);

  if (!safeCampaignKey || !safeUsername) {
    return '';
  }

  return `${AWARENESS_STORAGE_PREFIX}:${safeCampaignKey}:${safeUsername}`;
};

/**
 * Reads a stored expiry timestamp.
 *
 * Invalid or expired values are removed automatically.
 */
const getValidExpiry = (
  campaignKey,
  username
) => {
  const storageKey = getAwarenessStorageKey(
    campaignKey,
    username
  );

  if (!storageKey) return null;

  try {
    const rawValue = localStorage.getItem(storageKey);

    if (!rawValue) return null;

    const expiresAt = Number(rawValue);

    if (!Number.isFinite(expiresAt)) {
      localStorage.removeItem(storageKey);
      return null;
    }

    if (expiresAt <= Date.now()) {
      localStorage.removeItem(storageKey);
      return null;
    }

    return expiresAt;
  } catch (error) {
    console.warn(
      'Could not read awareness expiry:',
      error
    );

    return null;
  }
};

/**
 * Returns true while one specific campaign is cooling down.
 */
export const hasAwarenessCooldown = (
  campaignKey,
  username
) => {
  return Boolean(
    getValidExpiry(
      campaignKey,
      username
    )
  );
};

/**
 * Returns the campaign cooldown expiry timestamp.
 *
 * Useful for debugging or scheduling a later retry.
 */
export const getAwarenessCooldownUntil = (
  campaignKey,
  username
) => {
  return getValidExpiry(
    campaignKey,
    username
  );
};

/**
 * Starts a cooldown for one campaign.
 */
export const setAwarenessCooldown = ({
  campaignKey,
  username,
  durationMs,
}) => {
  const storageKey = getAwarenessStorageKey(
    campaignKey,
    username
  );

  const safeDurationMs = Number(durationMs);

  if (
    !storageKey ||
    !Number.isFinite(safeDurationMs) ||
    safeDurationMs <= 0
  ) {
    return null;
  }

  const cooldownUntil =
    Date.now() + safeDurationMs;

  try {
    localStorage.setItem(
      storageKey,
      String(cooldownUntil)
    );

    return cooldownUntil;
  } catch (error) {
    console.warn(
      'Could not save awareness cooldown:',
      error
    );

    return null;
  }
};

/**
 * Clears one campaign's cooldown.
 */
export const clearAwarenessCooldown = (
  campaignKey,
  username
) => {
  const storageKey = getAwarenessStorageKey(
    campaignKey,
    username
  );

  if (!storageKey) return;

  try {
    localStorage.removeItem(storageKey);
  } catch (error) {
    console.warn(
      'Could not clear awareness cooldown:',
      error
    );
  }
};

/**
 * Returns true while the shared popup gap is active.
 *
 * During this time, no awareness campaign should open.
 */
export const hasGlobalAwarenessGap = (
  username
) => {
  return hasAwarenessCooldown(
    GLOBAL_GAP_CAMPAIGN_KEY,
    username
  );
};

/**
 * Returns the global gap expiry timestamp.
 */
export const getGlobalAwarenessGapUntil = (
  username
) => {
  return getAwarenessCooldownUntil(
    GLOBAL_GAP_CAMPAIGN_KEY,
    username
  );
};

/**
 * Starts the shared gap after any awareness popup:
 *
 * - is dismissed
 * - is successfully completed
 *
 * Example duration:
 * 5 * 60 * 1000
 */
export const setGlobalAwarenessGap = ({
  username,
  durationMs,
}) => {
  return setAwarenessCooldown({
    campaignKey: GLOBAL_GAP_CAMPAIGN_KEY,
    username,
    durationMs,
  });
};

/**
 * Clears the shared global popup gap.
 *
 * Mostly useful for testing.
 */
export const clearGlobalAwarenessGap = (
  username
) => {
  clearAwarenessCooldown(
    GLOBAL_GAP_CAMPAIGN_KEY,
    username
  );
};
